import {
  createRouter,
  createRoute,
  createRootRoute,
  redirect,
  Link,
  Outlet,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AiCioPanel, AiCioTrigger, Route as AiCioPage } from '@modules/ai-cio';
import { clientRequestsSearchSchema } from '@modules/client-requests/search-schema';
import { EnpsSurveyHost } from '@modules/enps';
import { CompletePage as IntegrationsCompletePage } from '@modules/integrations';
import { invoicesSearchSchema } from '@modules/invoices/search-schema';
import { transactionsSearchSchema } from '@modules/transactions/search-schema';
import { bootstrapToken } from '@shared/auth/auth-route';
import { clearImpersonation } from '@shared/auth/impersonation';
import { isImpersonating, isTokenValid } from '@shared/auth/token.store';
import { AppShell } from '@shared/components/layout/AppShell';
import { requirePermission } from '@shared/guards/require-permission';
import { SECURITY_ROLES, requireRole } from '@shared/guards/require-role';
import { useTenantFavicon } from '@shared/hooks/use-tenant-favicon';
import { lazyWithRetry } from '@shared/lib/chunk-recovery';
import { provideVaultLinks } from '@shared/lib/vault-links';

import { useVaultFileChatDock } from './vault-file-chat-dock';
import { createAppVaultLinks, VaultLinksFlagSync } from './vault-links';

// ── Auth pages ────────────────────────────────────────────────────────────────
const LoginPage = lazyWithRetry(() =>
  import('@modules/auth').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazyWithRetry(() =>
  import('@modules/auth').then((m) => ({ default: m.ForgotPasswordPage })),
);
const CheckEmailPage = lazyWithRetry(() =>
  import('@modules/auth').then((m) => ({ default: m.CheckEmailPage })),
);
const ResetPasswordPage = lazyWithRetry(() =>
  import('@modules/auth').then((m) => ({ default: m.ResetPasswordPage })),
);
const ChooseCompanyPage = lazyWithRetry(() =>
  import('@modules/auth').then((m) => ({ default: m.ChooseCompanyPage })),
);
const TradeSystemSsoPage = lazyWithRetry(() =>
  import('@modules/auth').then((m) => ({ default: m.TradeSystemSsoPage })),
);

// ── Existing module pages ─────────────────────────────────────────────────────
const DashboardPage = lazyWithRetry(() =>
  import('@modules/dashboard').then((m) => ({ default: m.Route })),
);
const AnalyticsPage = lazyWithRetry(() =>
  import('@modules/analytics').then((m) => ({ default: m.Route })),
);
const WealthPage = lazyWithRetry(() =>
  import('@modules/wealth').then((m) => ({ default: m.Route })),
);
const ReportsPage = lazyWithRetry(() =>
  import('@modules/reports').then((m) => ({ default: m.Route })),
);
const MarketplacePage = lazyWithRetry(() =>
  import('@modules/marketplace').then((m) => ({ default: m.Route })),
);
const ClientRequestsPage = lazyWithRetry(() =>
  import('@modules/client-requests').then((m) => ({ default: m.Route })),
);
const TasksPage = lazyWithRetry(() => import('@modules/tasks').then((m) => ({ default: m.Route })));
const InvoicesPage = lazyWithRetry(() =>
  import('@modules/invoices').then((m) => ({ default: m.Route })),
);
const InvoicesStripePage = lazyWithRetry(() =>
  import('@modules/invoices').then((m) => ({ default: m.StripeConnectionPage })),
);
const MarketingPage = lazyWithRetry(() =>
  import('@modules/marketing').then((m) => ({ default: m.Route })),
);
const SecurityUsersPage = lazyWithRetry(() =>
  import('@modules/security').then((m) => ({ default: m.SecurityUsersPage })),
);
const SecurityRolesPage = lazyWithRetry(() =>
  import('@modules/security').then((m) => ({ default: m.SecurityRolesPage })),
);
const SecurityLogsPage = lazyWithRetry(() =>
  import('@modules/security').then((m) => ({ default: m.SecurityLogsPage })),
);
const SecuritySessionsPage = lazyWithRetry(() =>
  import('@modules/security').then((m) => ({ default: m.SecuritySessionsPage })),
);
const BpmsPage = lazyWithRetry(() => import('@modules/bpms').then((m) => ({ default: m.Route })));
const AdminPage = lazyWithRetry(() => import('@modules/admin').then((m) => ({ default: m.Route })));

// ── New module pages ──────────────────────────────────────────────────────────
const CalendarPage = lazyWithRetry(() =>
  import('@modules/calendar').then((m) => ({ default: m.Route })),
);
const MessengerPage = lazyWithRetry(() =>
  import('@modules/messenger').then((m) => ({ default: m.Route })),
);
const TransactionsPage = lazyWithRetry(() =>
  import('@modules/transactions').then((m) => ({ default: m.Route })),
);
const NewTabPage = lazyWithRetry(() =>
  import('@modules/new-tab').then((m) => ({ default: m.Route })),
);
const InvestorsPage = lazyWithRetry(() =>
  import('@modules/wealth/investors').then((m) => ({ default: m.Route })),
);
const NewInvestorPage = lazyWithRetry(() =>
  import('@modules/wealth/investors/new').then((m) => ({ default: m.Route })),
);
const InvestorCardPage = lazyWithRetry(() =>
  import('@modules/wealth/investors/card').then((m) => ({ default: m.Route })),
);
const VaultPage = lazyWithRetry(() => import('@modules/vault').then((m) => ({ default: m.Route })));
const VaultFilePage = lazyWithRetry(() =>
  import('@modules/vault').then((m) => ({ default: m.FileRoute })),
);
const AccountsPage = lazyWithRetry(() =>
  import('@modules/wealth/accounts').then((m) => ({ default: m.Route })),
);
const SettingsPage = lazyWithRetry(() =>
  import('@modules/settings').then((m) => ({ default: m.Route })),
);
const SettingsProfilePage = lazyWithRetry(() =>
  import('@modules/settings').then((m) => ({ default: m.ProfileRoute })),
);
const SettingsAiCustomizationPage = lazyWithRetry(() =>
  import('@modules/settings').then((m) => ({ default: m.AiCustomizationRoute })),
);
const SettingsCompanyPage = lazyWithRetry(() =>
  import('@modules/settings').then((m) => ({ default: m.CompanyRoute })),
);
const SettingsBillingPage = lazyWithRetry(() =>
  import('@modules/settings').then((m) => ({ default: m.BillingRoute })),
);

// ── Stub page factory ─────────────────────────────────────────────────────────
function stubPage(titleKey: string, titleDefault: string) {
  return function StubPage() {
    const { t } = useTranslation('common');
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{t(titleKey, titleDefault)}</h1>
        <p className="mt-2 text-muted-foreground">{t('stub.comingSoon', 'Coming soon')}</p>
      </div>
    );
  };
}

// ── App shell with AI CIO surfaces injected (module-boundary: app → domain) ──
// The eNPS survey host rides along: it renders nothing unless a survey is due.
function AppShellWithAiCio() {
  return (
    <>
      <AppShell aiPanel={<AiCioPanel />} aiTrigger={<AiCioTrigger />} />
      <EnpsSurveyHost />
      <VaultLinksFlagSync />
    </>
  );
}

// Fullscreen layout: authenticated but WITHOUT the app shell (nav + top bar).
// Used by focused, chrome-less surfaces like the Vault file editor.
function FullscreenLayout() {
  useTenantFavicon();
  return (
    <>
      <Outlet />
      <VaultLinksFlagSync />
    </>
  );
}

// Shared auth guard for protected layout routes: restore the session if needed,
// otherwise bounce to /login. Extracted so both the shell and the fullscreen
// layout enforce the exact same rule. `url` carries the page's path AND query,
// so a deep link (/sso/trade-system?redirect_uri=…) survives the login.
async function ensureAuthenticated({
  location,
}: {
  location: { pathname: string; searchStr: string };
}) {
  if (isTokenValid()) return;
  try {
    await bootstrapToken();
  } catch {
    // Any failure to restore the session (expired/invalid refresh cookie,
    // server/network error, or no token returned) falls through to the
    // token check below — we treat "couldn't establish a session" uniformly.
  }
  // Never render a protected layout without a valid token: a stale `auth-user`
  // in localStorage must not be enough to pass the route guards.
  if (!isTokenValid()) {
    if (isImpersonating()) {
      clearImpersonation();
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', reloadDocument: true });
    }
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/login',
      search: { redirect: 'true', url: `${location.pathname}${location.searchStr}` },
    });
  }
}

// ── Root ──────────────────────────────────────────────────────────────────────
const rootRoute = createRootRoute();

// ── Public routes ─────────────────────────────────────────────────────────────
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const checkEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/check-email',
  component: CheckEmailPage,
  validateSearch: (search: Record<string, unknown>): { email: string } => ({
    email: typeof search.email === 'string' ? search.email : '',
  }),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token: string; email: string } => ({
    token: typeof search.token === 'string' ? search.token : '',
    email: typeof search.email === 'string' ? search.email : '',
  }),
});

// DEV-4200: interstitial `AuthProvider.login()` routes to instead of the final
// destination when the signed-in user has more than one available company.
// Needs a valid token (it queries `me`) but sits outside `appRoute` — it's
// deliberately chrome-less (no sidebar/shell) like the other auth screens.
const chooseCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/choose-company',
  component: ChooseCompanyPage,
  validateSearch: (search: Record<string, unknown>): { url?: string } => ({
    url: typeof search.url === 'string' ? search.url : undefined,
  }),
  beforeLoad: () => {
    if (!isTokenValid()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
});

// Where every connected-account Connect ends (ity-platform-backend
// `integrationConnect.service.ts::completeUrl`): the sign-in tab reports the result to the CRM's
// other tabs and closes itself. Public and chrome-less, outside the shell, so a tab with no
// restored session still reports (CC D16).
const integrationsCompleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations/complete',
  validateSearch: (search: Record<string, unknown>): { service?: string; result?: string } => ({
    service: typeof search.service === 'string' ? search.service : undefined,
    result: typeof search.result === 'string' ? search.result : undefined,
  }),
  component: function IntegrationsComplete() {
    const { service, result } = integrationsCompleteRoute.useSearch();
    return <IntegrationsCompletePage service={service} result={result} />;
  },
});

// ── Protected: App shell ──────────────────────────────────────────────────────
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppShellWithAiCio,
  beforeLoad: ensureAuthenticated,
});

// ── Protected: Fullscreen (no shell) ──────────────────────────────────────────
const fullscreenRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'fullscreen',
  component: FullscreenLayout,
  beforeLoad: ensureAuthenticated,
});

// ── Home redirect: / → /dashboard ─────────────────────────────────────────────
// Deliberately NOT /ai-cio. The landing page must work in every environment this
// bundle is deployed to, and the AI CIO section only works where an orchestrator
// is reachable behind /ai-cio/api -- which investbanq-prod has none of. Sending
// every user there on login would have made prod look broken while the rest of
// the CRM was fine: the page renders, its API calls fall through to the SPA
// fallback and come back as HTML. Anyone who wants the assistant still has it in
// the sidebar. Revisit only once /ai-cio is reachable everywhere this ships.
const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/dashboard', replace: true });
  },
});

/**
 * The Investor Profile's section shortcuts land on a section with its Investor
 * filter already pre-applied (DEV-3780). Dashboard and Analytics keep their
 * filter state in the page (not the URL), so they read this hand-off only as
 * the initial selection; `investorName` labels the filter chip for an investor
 * the destination's own option list cannot name — see `withPinnedOption`.
 */
interface InvestorDeepLinkSearch {
  investorIds?: string[];
  investorName?: string;
}

const parseInvestorDeepLink = (search: Record<string, unknown>): InvestorDeepLinkSearch => ({
  investorIds:
    Array.isArray(search.investorIds) && search.investorIds.every((id) => typeof id === 'string')
      ? search.investorIds
      : undefined,
  investorName: typeof search.investorName === 'string' ? search.investorName : undefined,
});

// ── Dashboard ─────────────────────────────────────────────────────────────────
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: DashboardPage,
  validateSearch: parseInvestorDeepLink,
  beforeLoad: requirePermission('dashboard:read'),
});

// ── Analytics ─────────────────────────────────────────────────────────────────
const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/analytics',
  component: AnalyticsPage,
  validateSearch: parseInvestorDeepLink,
  beforeLoad: requirePermission('analytics:read'),
});

// ── Tasks ─────────────────────────────────────────────────────────────────────
const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tasks',
  component: TasksPage,
  beforeLoad: requirePermission('tasks:read'),
});

// ── Calendar ──────────────────────────────────────────────────────────────────
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calendar',
  component: CalendarPage,
  beforeLoad: requirePermission('calendar:read'),
});

// ── Messenger ─────────────────────────────────────────────────────────────────
const messengerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/messenger',
  component: MessengerPage,
  beforeLoad: requirePermission('messenger:read'),
});

// ── Administration ────────────────────────────────────────────────────────────
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  component: AdminPage,
  beforeLoad: requirePermission('admin:read'),
});

// ── BPMS ─────────────────────────────────────────────────────────────────────
const bpmsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/bpms',
  component: BpmsPage,
  beforeLoad: requirePermission('bpms:read'),
});

// ── Marketing ─────────────────────────────────────────────────────────────────
const marketingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/marketing',
  component: MarketingPage,
  beforeLoad: requirePermission('marketing:read'),
});

const marketingLeadsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/marketing/leads',
  component: stubPage('nav.marketingLeads', 'Leads'),
  beforeLoad: requirePermission('marketing:read'),
});

const marketingLandingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/marketing/landing',
  component: stubPage('nav.marketingLanding', 'Landing'),
  beforeLoad: requirePermission('marketing:read'),
});

const marketingPostRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/marketing/post',
  component: stubPage('nav.marketingPost', 'Post'),
  beforeLoad: requirePermission('marketing:read'),
});

// ── Client Requests ───────────────────────────────────────────────────────────
const clientRequestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/client-requests',
  component: ClientRequestsPage,
  // Filters (status/type/sub-type/responsible/investor/account/date range)
  // are persisted in these search params — see `useClientRequestFilters`.
  validateSearch: (search: Record<string, unknown>) => clientRequestsSearchSchema.parse(search),
  beforeLoad: requirePermission('client-requests:read'),
});

// ── Wealth Management ─────────────────────────────────────────────────────────
const wealthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wealth',
  component: WealthPage,
  beforeLoad: requirePermission('wealth:read'),
});

// ── Transactions ──────────────────────────────────────────────────────────────
const transactionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/transactions',
  component: TransactionsPage,
  // Filters (search/date range/account/investor/currency/exchange/source/type/
  // symbol/status/author/has-docs) are persisted in these search params — see
  // `useTransactionFilters`. The investor hand-off ("Add portfolio data",
  // the profile's Transactions tile, the creation success screen) simply
  // navigates with `investorIds` / `accountIds` already set.
  validateSearch: (search: Record<string, unknown>) => transactionsSearchSchema.parse(search),
  beforeLoad: requirePermission('transactions:read'),
});

// ── Marketplace ───────────────────────────────────────────────────────────────
const marketplaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/marketplace',
  component: MarketplacePage,
  beforeLoad: requirePermission('marketplace:read'),
});

// ── Reports ───────────────────────────────────────────────────────────────────
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  component: ReportsPage,
  beforeLoad: requirePermission('reports:read'),
});

// ── Invoices ──────────────────────────────────────────────────────────────────
const invoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/invoices',
  component: InvoicesPage,
  // Persists the filter bar (search/status/investor/providers/period) to the
  // URL — see `useInvoiceFilters`.
  validateSearch: (search: Record<string, unknown>) => invoicesSearchSchema.parse(search),
  beforeLoad: requirePermission('invoices:read'),
});

// The Stripe surface is reached from the Invoices header, not the navigation:
// the spec makes it a sub-page of Invoices rather than a top-level section.
// Same read permission — anyone who can see invoices can see how they get paid.
const invoicesStripeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/invoices/stripe',
  component: InvoicesStripePage,
  beforeLoad: requirePermission('invoices:read'),
});

// ── Security ──────────────────────────────────────────────────────────────────
// Four sibling pages; `/security` itself has no content and lands on Users.
//
// These gate on `requireRole`, not `requirePermission`, because the spec makes
// Security role-restricted ("only Director and Admin can access it") and
// `requirePermission` cannot enforce anything: the `Permission` strings it
// checks live only on the client, and the `me { role { permissions } }` field it
// reads them from does not exist in the schema, so it always allows. The token's
// `roles` claim is real. See shared/guards/require-role.ts.

const securityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security',
  beforeLoad: () => {
    requireRole(SECURITY_ROLES)();
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router signals redirects by throwing
    throw redirect({ to: '/security/users' });
  },
});

const securityUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security/users',
  component: SecurityUsersPage,
  beforeLoad: requireRole(SECURITY_ROLES),
});

const securityRolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security/roles',
  component: SecurityRolesPage,
  beforeLoad: requireRole(SECURITY_ROLES),
});

const securityLogsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security/logs',
  component: SecurityLogsPage,
  beforeLoad: requireRole(SECURITY_ROLES),
});

const securitySessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security/sessions',
  component: SecuritySessionsPage,
  beforeLoad: requireRole(SECURITY_ROLES),
});

// Back-compat: the old combined Audit page split into Logs + Sessions, and
// saved tabs / bookmarks still point here.
const securityAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security/audit',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router signals redirects by throwing
    throw redirect({ to: '/security/logs' });
  },
});

// ── Investors ─────────────────────────────────────────────────────────────────
// Persists the filter bar (search text + status/manager selection) to the URL
// so filters survive reloads/back-forward navigation. Every field is
// `.optional().catch(undefined)` rather than `.catch(default)` so an absent
// key stays absent (only genuinely malformed values fall back to a default) —
// otherwise `validateSearch` would re-fill a default-valued key on every
// navigation, including when `useUrlFilters` omits it to produce a clean URL
// after "Reset".
const investorsSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  statuses: z.array(z.string()).optional().catch(undefined),
  managers: z.array(z.string()).optional().catch(undefined),
});
type InvestorsSearch = z.infer<typeof investorsSearchSchema>;

const investorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wealth/investors',
  component: InvestorsPage,
  validateSearch: (search: Record<string, unknown>): InvestorsSearch =>
    investorsSearchSchema.parse(search),
  beforeLoad: requirePermission('wealth:read'),
});

/** The fields the new-client wizard accepts from the URL. Mirrors the
 *  `new_client` destination's `params` allowlist in the MCP registry. */
const NEW_INVESTOR_PREFILL = [
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'investorKind',
  'companyLegalName',
] as const;

type NewInvestorSearch = { step?: 2 | 3 } & Partial<
  Record<(typeof NEW_INVESTOR_PREFILL)[number], string>
>;

const newInvestorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wealth/investors/new',
  component: NewInvestorPage,
  // ?step keeps the wizard step in browser history so Back moves one step at a
  // time (and only leaves the flow from step 1) instead of exiting immediately.
  //
  // The rest are the AI CIO hand-off (DEV-0000): the assistant cannot create a
  // client with accounts itself, so it collects what it can in conversation and
  // opens this page with those values in the form. Hand-off by URL is this app's
  // own convention — see the transactions route below, which the investor
  // "Add portfolio data" flow navigates to with ids already set.
  //
  // **This list is a contract.** The MCP registry pins the same field names as
  // that destination's allowlist and drops anything else before the card is
  // built, so a name added here without adding it there is never sent, and a
  // name there without a reader here is silently ignored. Keep them in step.
  validateSearch: (search: Record<string, unknown>): NewInvestorSearch => {
    const step = Number(search.step);
    const out: NewInvestorSearch = step === 2 || step === 3 ? { step } : {};
    for (const key of NEW_INVESTOR_PREFILL) {
      const value = search[key];
      if (typeof value === 'string' && value !== '') out[key] = value;
    }
    return out;
  },
  beforeLoad: requirePermission('wealth:read'),
});

const investorCardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wealth/investors/$id',
  component: InvestorCardPage,
  beforeLoad: requirePermission('wealth:read'),
});

// ── Accounts ──────────────────────────────────────────────────────────────────
const accountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wealth/accounts',
  component: AccountsPage,
  // ?investor pre-applies the Investor filter (profile deep-link); ?create=1
  // opens the New Account side panel (Nav-bar entry point); ?account=<id>
  // opens that account's side panel (profile Accounts section row click).
  validateSearch: (
    search: Record<string, unknown>,
  ): { investor?: string; create?: boolean; account?: string } => ({
    investor: typeof search.investor === 'string' ? search.investor : undefined,
    account: typeof search.account === 'string' ? search.account : undefined,
    // The router JSON-parses raw params, so ?create=1 arrives as the number 1.
    create: search.create === true || search.create === 1 || search.create === '1' || undefined,
  }),
  beforeLoad: requirePermission('wealth:read'),
});

// ── Vault ─────────────────────────────────────────────────────────────────────
const vaultReadGuard = requirePermission('vault:read');

// Enforce the permission, then activate the real Vault backend (swap the mock
// for the GraphQL service). Dynamic import keeps the vault module lazily
// chunked; running here (app layer, pre-render) means the first fetch already
// hits the backend, and component unit tests — which render without the router
// — stay on the mock.
const vaultBeforeLoad = async (): Promise<void> => {
  vaultReadGuard();
  const { initVault } = await import('@modules/vault');
  initVault();
};

const vaultRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/vault',
  component: VaultPage,
  // List filters live in the URL so back-navigation and the tab bar restore
  // them. `?investorId`/`?investorName` are the Investor Profile hand-off
  // (DEV-3780): they pre-apply the Investors filter to that investor.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    tab?: 'personal' | 'company';
    page?: number;
    investorId?: string;
    investorName?: string;
  } => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    tab: search.tab === 'company' || search.tab === 'personal' ? search.tab : undefined,
    page: typeof search.page === 'number' ? search.page : undefined,
    investorId: typeof search.investorId === 'string' ? search.investorId : undefined,
    investorName: typeof search.investorName === 'string' ? search.investorName : undefined,
  }),
  beforeLoad: vaultBeforeLoad,
});

function VaultFileWithAiCio() {
  return <VaultFilePage chatDock={useVaultFileChatDock()} />;
}

// File view/editor is fullscreen (outside the app shell) per design.
const vaultFileRoute = createRoute({
  getParentRoute: () => fullscreenRoute,
  path: '/vault/$fileId',
  component: VaultFileWithAiCio,
  validateSearch: (search: Record<string, unknown>): { c?: string } => ({
    c: typeof search.c === 'string' && search.c !== '' ? search.c : undefined,
  }),
  beforeLoad: vaultBeforeLoad,
});

// ── Trade System sign-in ──────────────────────────────────────────────────────
// Trade System's login page sends a CRM user here with its own callback as
// `redirect_uri`; the page signs them in there with a hand-off token. Signed-in
// and chrome-less, like the other auth screens.
const tradeSystemSsoRoute = createRoute({
  getParentRoute: () => fullscreenRoute,
  path: '/sso/trade-system',
  component: TradeSystemSsoPage,
  validateSearch: (search: Record<string, unknown>): { redirect_uri?: string } => ({
    redirect_uri: typeof search.redirect_uri === 'string' ? search.redirect_uri : undefined,
  }),
});

// ── AI CIO ────────────────────────────────────────────────────────────────────
const aiCioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ai-cio',
  component: AiCioPage,
  // ?c binds the tab to a conversation so multiple AI CIO chats can live in tabs.
  validateSearch: (search: Record<string, unknown>): { c?: string } => ({
    c: typeof search.c === 'string' ? search.c : undefined,
  }),
  beforeLoad: requirePermission('ai-cio:read'),
});

// ── New Tab ───────────────────────────────────────────────────────────────────
const newTabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/new-tab',
  component: NewTabPage,
});

// ── Settings ──────────────────────────────────────────────────────────────────
// Personal-scope pages (no module permission beyond auth); layout route mounts
// the settings service provider once so edits persist across the child pages.
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsPage,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/settings/profile', replace: true });
  },
});

const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile',
  component: SettingsProfilePage,
});

const settingsAiCustomizationRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/ai-customization',
  component: SettingsAiCustomizationPage,
});

const settingsCompanyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/company',
  component: SettingsCompanyPage,
  validateSearch: (search: Record<string, unknown>): { section?: string } => ({
    section: typeof search.section === 'string' ? search.section : undefined,
  }),
});

const settingsBillingRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/billing',
  component: SettingsBillingPage,
});

// ── Unauthorized ──────────────────────────────────────────────────────────────
const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/unauthorized',
  component: function UnauthorizedPage() {
    const { t } = useTranslation('common');
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">{t('unauthorized.title', '403 — Access Denied')}</h1>
        <p className="text-muted-foreground">
          {t('unauthorized.description', 'You do not have permission to view this page.')}
        </p>
        <Link to="/dashboard" className="text-primary underline">
          {t('unauthorized.goToDashboard', 'Go to Dashboard')}
        </Link>
      </div>
    );
  },
});

// ── Route tree ────────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  checkEmailRoute,
  resetPasswordRoute,
  chooseCompanyRoute,
  unauthorizedRoute,
  integrationsCompleteRoute,
  appRoute.addChildren([
    homeRoute,
    dashboardRoute,
    analyticsRoute,
    tasksRoute,
    calendarRoute,
    messengerRoute,
    adminRoute,
    bpmsRoute,
    marketingRoute,
    marketingLeadsRoute,
    marketingLandingRoute,
    marketingPostRoute,
    clientRequestsRoute,
    wealthRoute,
    investorsRoute,
    newInvestorRoute,
    investorCardRoute,
    accountsRoute,
    transactionsRoute,
    vaultRoute,
    marketplaceRoute,
    reportsRoute,
    invoicesRoute,
    invoicesStripeRoute,
    securityRoute,
    securityUsersRoute,
    securityRolesRoute,
    securityLogsRoute,
    securitySessionsRoute,
    securityAuditRoute,
    aiCioRoute,
    newTabRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      settingsProfileRoute,
      settingsAiCustomizationRoute,
      settingsCompanyRoute,
      settingsBillingRoute,
    ]),
  ]),
  fullscreenRoute.addChildren([vaultFileRoute, tradeSystemSsoRoute]),
]);

// ── Router ────────────────────────────────────────────────────────────────────
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

provideVaultLinks(
  createAppVaultLinks((fileId, c) => {
    void router.navigate({ to: '/vault/$fileId', params: { fileId }, search: c ? { c } : {} });
  }),
);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
